/**
 * SiteQrSettingsCard — company-level Clock-In Verification defaults
 * (Jun 2026). Admin-only card on Settings → Company Information.
 *
 * Controls whether new jobs with an address automatically get a Site QR
 * code (default OFF — one-time service calls shouldn't require scanning).
 * Individual jobs can always have their QR added or removed from the
 * job's Site QR dialog.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { QrCode, Loader2, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.__BACKEND_URL__}/api`;

export default function SiteQrSettingsCard({ token }) {
  const [autoQr, setAutoQr] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [rotating, setRotating] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const rotateAll = async () => {
    setRotating(true);
    try {
      const r = await axios.post(`${API}/company/site-qr-rotate-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setConfirmRotate(false);
      toast.success(`${r.data.rotated} QR code${r.data.rotated === 1 ? '' : 's'} rotated — old posters no longer work. Reprint from Jobs → QR Posters.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not rotate QRs');
    } finally {
      setRotating(false);
    }
  };

  const clearAll = async () => {
    setClearing(true);
    try {
      const r = await axios.post(`${API}/company/site-qr-settings/clear-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setConfirmClear(false);
      toast.success(`QR requirement removed from ${r.data.cleared} active job${r.data.cleared === 1 ? '' : 's'}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not clear QRs');
    } finally {
      setClearing(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/company/site-qr-settings`, { headers: { Authorization: `Bearer ${token}` } });
      setAutoQr(!!r.data.auto_generate_on_job_creation);
    } catch {
      /* non-admin or transient error — leave default */
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async (value) => {
    setSaving(true);
    setAutoQr(value);
    try {
      await axios.put(`${API}/company/site-qr-settings`, { auto_generate_on_job_creation: value }, { headers });
      toast.success(value
        ? 'New jobs with an address will now get a Site QR code automatically.'
        : 'Auto QR is off — jobs only get a QR code when you add one manually.');
    } catch (err) {
      setAutoQr(!value);
      toast.error(err.response?.data?.detail || 'Could not save setting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="shadow-lg mb-6" data-testid="site-qr-settings-card">
      <CardHeader className="bg-[#1A2433] border-b">
        <CardTitle className="text-lg font-bold flex items-center space-x-2">
          <QrCode className="w-5 h-5" />
          <span>Clock-In Verification (Site QR)</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        {loading ? (
          <div className="flex items-center text-sm text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-white">Auto-generate Site QR for new jobs</p>
                <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                  When on, every new job with an address gets a QR code and workers are prompted to
                  scan it at clock-in. Keep this <b>off</b> if most of your jobs are one-time service
                  calls — you can still add a QR to any individual job from its "Site QR" dialog, and
                  remove one that was added by mistake.
                </p>
              </div>
              <div className="flex items-center gap-2 pt-1">
                {saving && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                <Switch
                  checked={autoQr}
                  onCheckedChange={save}
                  disabled={saving}
                  data-testid="site-qr-auto-switch"
                />
              </div>
            </div>

            <div className="rounded-lg p-3" style={{ border: '1px solid rgba(30,136,229,0.3)', background: 'rgba(30,136,229,0.04)' }}>
              {confirmRotate ? (
                <div className="space-y-2">
                  <p className="text-xs" style={{ color: '#7BB8E8' }}>
                    Rotate the QR code on ALL active jobs? Every printed poster immediately stops
                    working and you'll need to reprint them (Jobs → QR Posters → Print all active).
                    Use this when posters have gone missing or you suspect codes were shared.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={rotateAll}
                      disabled={rotating}
                      className="bg-[#1E88E5] hover:bg-[#1976D2] text-white"
                      data-testid="site-qr-rotate-all-confirm-btn"
                    >
                      {rotating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                      Yes, rotate all
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setConfirmRotate(false)} data-testid="site-qr-rotate-all-cancel-btn">
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmRotate(true)}
                  className="text-xs font-medium flex items-center gap-1.5"
                  style={{ color: '#7BB8E8' }}
                  data-testid="site-qr-rotate-all-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Rotate all QR codes (posters missing or stale — invalidates old posters)
                </button>
              )}
            </div>

            <div className="rounded-lg p-3" style={{ border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.04)' }}>
              {confirmClear ? (
                <div className="space-y-2">
                  <p className="text-xs" style={{ color: '#FCA5A5' }}>
                    Remove the QR requirement from ALL active jobs? Workers will no longer be asked
                    to scan on any of them. Completed/invoiced jobs are untouched, and you can re-add
                    a QR to any job later.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={clearAll}
                      disabled={clearing}
                      className="bg-red-600 hover:bg-red-700 text-white"
                      data-testid="site-qr-clear-all-confirm-btn"
                    >
                      {clearing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
                      Yes, clear all
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setConfirmClear(false)} data-testid="site-qr-clear-all-cancel-btn">
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmClear(true)}
                  className="text-xs font-medium flex items-center gap-1.5"
                  style={{ color: '#F87171' }}
                  data-testid="site-qr-clear-all-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Remove QR requirement from all active jobs (fix mistakes in bulk)
                </button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
